const EXCEL_MAX_ROW = 1_048_576;

/**
 * Genera etiquetas de columna estilo Excel (A..Z, AA..AZ, ...).
 * @param {number} maxColumnas Número máximo de columnas a generar
 * @returns {string[]} Array de etiquetas de columnas
 */
export const getAlphabet = (maxColumnas = 500) => {
  const cantidadColumnas = Number(maxColumnas);
  if (!Number.isFinite(cantidadColumnas) || cantidadColumnas <= 0) return [];

  const indiceAColumna = indice => {
    if (indice < 0) return '';
    const letra = String.fromCodePoint((indice % 26) + 65);
    const prefijo = indiceAColumna(Math.floor(indice / 26) - 1);
    return prefijo + letra;
  };

  return Array.from({ length: cantidadColumnas }, (_, i) => indiceAColumna(i));
};

/**
 * Convierte un número (basado en 1) a la etiqueta de columna de Excel (A=1, B=2, ..., Z=26, AA=27, etc.).
 * Esto es útil para calcular rangos dinámicos o referencias de celdas en ExcelJS.
 * @param {number} numero - Número de la columna (1 para 'A', 2 para 'B', etc.). Si es <= 0, devuelve ''.
 * @returns {string} Etiqueta de la columna en mayúsculas.
 * @example
 * numeroAColumnaExcel(1) // 'A'
 * numeroAColumnaExcel(26) // 'Z'
 * numeroAColumnaExcel(27) // 'AA'
 * numeroAColumnaExcel(52) // 'AZ'
 * numeroAColumnaExcel(53) // 'BA'
 */
export const numeroAColumnaExcel = numero => {
  if (numero <= 0) return '';
  return (
    numeroAColumnaExcel(Math.floor((numero - 1) / 26)) +
    String.fromCodePoint(65 + ((numero - 1) % 26))
  );
};

/**
 * Reemplaza los valores 0 con '--' en columnas numéricas de una hoja de ExcelJS.
 * Las columnas numéricas se identifican por t: 'number', 'money' o 'numeroConMiles'.
 * @param {object} hoja Hoja de ExcelJS (worksheet)
 * @param {array} columnas Array de definiciones de columnas
 * @param {number} filaInicio Fila donde inicia la tabla de datos
 * @param {number} longitudDatos Número de filas de datos
 */
export const reemplazarCerosEnHoja = (
  hoja,
  columnas,
  filaInicio,
  longitudDatos
) => {
  if (longitudDatos <= 0) return;

  const indicesColumnasNumericas = columnas
    .map((columna, indice) =>
      ['number', 'money', 'numeroConMiles'].includes(columna.t)
        ? indice + 1
        : null
    )
    .filter(Boolean);

  for (let i = 0; i < longitudDatos; i++) {
    const fila = hoja.getRow(filaInicio + 1 + i);
    indicesColumnasNumericas.forEach(indice => {
      const celda = fila.getCell(indice);
      if (celda.value === 0) {
        celda.value = '--';
        celda.alignment = { horizontal: 'center' };
      }
    });
  }
};

/**
 * Agrega una validación de lista desplegable a una columna del worksheet
 * @param {object} ws - Worksheet de ExcelJS
 * @param {number} indiceColumna - Índice 1-based de la columna
 * @param {string} formulae - Opciones de la lista en formato '"op1,op2"'
 * @param {number} filaInicio - Fila donde inicia la validación (por defecto 2)
 */
export const agregarValidacionLista = (
  ws,
  indiceColumna,
  formulae,
  filaInicio = 2
) => {
  const col = numeroAColumnaExcel(indiceColumna);
  ws.dataValidations.add(`${col}${filaInicio}:${col}${EXCEL_MAX_ROW}`, {
    type: 'list',
    allowBlank: true,
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: 'Valor no permitido',
    error: 'Por favor seleccione una opción disponible en la lista.',
    formulae: [formulae],
  });
};

/**
 * Desbloquea las columnas de datos y bloquea la fila de encabezado
 * @param {object} ws - Worksheet de ExcelJS
 * @param {number} totalColumnas - Total de columnas a configurar
 * @param {number} filaEncabezado - Fila del encabezado que debe permanecer bloqueada
 */
export const configurarProteccionCeldas = (
  ws,
  totalColumnas,
  filaEncabezado
) => {
  for (let col = 1; col <= totalColumnas; col++) {
    const column = ws.getColumn(col);
    column.style = { ...column.style, protection: { locked: false } };
  }
  for (let col = 1; col <= totalColumnas; col++) {
    ws.getCell(filaEncabezado, col).protection = { locked: true };
  }
};

/**
 * Convierte una hoja de ExcelJS a un array de objetos usando la primera fila como encabezados.
 * @param {Worksheet} worksheet - Hoja de ExcelJS
 * @returns {Array<Object>} Array de objetos, cada uno representa una fila de datos
 */
export function hojaAJson(worksheet, skipRows = 0) {
  const filas = [];
  let encabezadosFila = null;
  let filasSaltadas = 0;

  worksheet.eachRow({ includeEmpty: false }, (fila, numeroFila) => {
    if (filasSaltadas < skipRows) {
      filasSaltadas++;
      return;
    }

    const valoresFila = fila.values.slice(1);

    if (encabezadosFila === null) {
      encabezadosFila = valoresFila.map(valor =>
        typeof valor === 'string' ? valor.trim() : String(valor || '').trim()
      );
      return;
    }

    const filaObjeto = encabezadosFila.reduce(
      (acumulador, encabezado, indice) => {
        return {
          ...acumulador,
          [encabezado]:
            valoresFila[indice] !== undefined ? valoresFila[indice] : '',
        };
      },
      {}
    );

    filas.push(filaObjeto);
  });

  return filas;
}
